import React, { useEffect, useState } from 'react';
import LazyLoad from 'react-lazyload';
import s from './CategoryDisplay.module.css';
import ShopifyCollections from '../../modules/shopify-collections/shopify-collections';
import loading from '../../images/loading.gif';
import notFound from '../../images/image-not-found.svg';
import { IProduct } from 'shopify-api-node';
import SwatchData, { SwatchDataResponse, SwatchDataSingle } from '../../modules/swatch-data/swatch-data';
import { FiltersOpen, FilterPanel, Filter, FiltersSelected } from '../FilterPanel/FilterPanel';

export type CategoryDisplayProps = {
  collectionId: string | number;
}
export enum SortOptions {
  ALPHABETICALLY_AZ = 'ALPHABETICALLY_AZ',
  ALPHABETICALLY_ZA = 'ALPHABETICALLY_ZA',
  PRICE_LOW_TO_HIGH = 'PRICE_LOW_TO_HIGH',
  PRICE_HIGH_TO_LOW = 'PRICE_HIGH_TO_LOW',
  DATE_OLD_TO_NEW = 'DATE_OLD_TO_NEW',
  DATE_NEW_TO_OLD = 'DATE_NEW_TO_OLD',
}
const sortAlphabeticallyAZ = (a: IProduct, b: IProduct): number => {
  if (a.title < b.title) {
    return -1;
  }
  if (a.title > b.title) {
    return 1;
  }
  return 0
};
const sortAlphabeticallyZA = (a: IProduct, b: IProduct): number => {
  if (a.title < b.title) {
    return 1;
  }
  if (a.title > b.title) {
    return -1;
  }
  return 0
};
const sortPriceHighToLow = (a: IProduct, b: IProduct): number => {
  return -1 * ((parseFloat(a.variants[0].price) || 0) - (parseFloat(b.variants[0].price) || 0))
}
const sortPriceLowToHigh = (a: IProduct, b: IProduct): number => {
  return (parseFloat(a.variants[0].price) || 0) - (parseFloat(b.variants[0].price) || 0)
}
const sortDateOldToNew = (a: IProduct, b: IProduct): number => {
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
}
const sortDateNewToOld = (a: IProduct, b: IProduct): number => {
  return -1 * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
}

const CategoryDisplay: React.FC<CategoryDisplayProps> = ({ collectionId }) => {
  const [state, setState] = useState({
    filteredProducts: [] as IProduct[],
    products: [] as IProduct[],
    isLoading: true,
    colors: [] as string[],
    colorData: [] as SwatchDataSingle[],
    filterData: [] as Filter[],
    filtersSelected: {} as FiltersSelected,
    groupData: [] as SwatchDataResponse["groups"],
  });
  const [appliedSortingMethod, setAppliedSortingMethod] = useState(SortOptions.ALPHABETICALLY_AZ);
  const [selectedColor, setSelectedColor_] = useState('')
  const [openFilters, setOpenFilters] = useState({ color: false } as FiltersOpen)
  const setSelectedColor = (color: string) => {
    setSelectedColor_(color);
    if (color) {
      const openFiltersNew = { ...openFilters };
      openFiltersNew['color'] = false;
      setOpenFilters(openFiltersNew);
    }
  }
  const {
    isLoading,
    colors,
    filteredProducts,
    colorData,
    products,
    filterData,
    filtersSelected,
    groupData,
  } = state;

  useEffect(() => {
    (async () => {
      let shopifyCollections = new ShopifyCollections();
      let swatchData = new SwatchData();
      const swatches = await swatchData.get();
      const data = await shopifyCollections.get(collectionId);
      if (!data) {
        return;
      }
      const filterData = [] as Filter[];
      const colorSet = new Set<string>();

      data.products.forEach(product => {
        product.variants &&
          product.variants.forEach(variant => {
            const variantColor = variant.option1;
            const foundColorGroup = !variantColor ? false : swatches.groups.find(g => 
              g.colors.find(color => color.toLowerCase() === variantColor.toLowerCase() )
            );
            if (foundColorGroup) {
              colorSet.add(foundColorGroup.title);
            }
          })

        if (product.vendor && product.vendor !== 'unknown-vendor') {
          const foundFilterIndex = filterData.findIndex((x: Filter) => x.name === 'Brand');
          if (foundFilterIndex === -1) {
            filterData.push({
              name: 'Brand',
              options: [{ name: product.vendor, count: 1 }]
            })
          } else {
            const foundFilterOptionIndex = filterData[foundFilterIndex].options.findIndex(x => x.name === product.vendor);
            if (foundFilterOptionIndex === -1) {
              filterData[foundFilterIndex].options.push({
                name: product.vendor,
                count: 1,
              })
            } else {
              filterData[foundFilterIndex].options[foundFilterOptionIndex].count += 1
            }
          }
        }

        if (product.product_type) {
          const foundFilterIndex = filterData.findIndex((x: Filter) => x.name === 'Gender');
          if (foundFilterIndex === -1) {
            filterData.push({
              name: 'Gender',
              options: [{ name: product.product_type, count: 1 }]
            })
          } else {
            const foundFilterOptionIndex = filterData[foundFilterIndex].options.findIndex(x => x.name === product.product_type);
            if (foundFilterOptionIndex === -1) {
              filterData[foundFilterIndex].options.push({
                name: product.product_type,
                count: 1,
              })
            } else {
              filterData[foundFilterIndex].options[foundFilterOptionIndex].count += 1
            }
          }
        }

        product.tags.split(',').forEach((tag) => {
          if (tag.indexOf('__filter:') === -1) return;
          const [filterName, filterValue] = tag.split('__filter:')[1].split('--');
          const foundFilterIndex = filterData.findIndex((x: Filter) => x.name === filterName);
          if (foundFilterIndex === -1) {
            return filterData.push({
              name: filterName,
              options: [{ name: filterValue, count: 1 }]
            })
          }

          const foundFilterOptionIndex = filterData[foundFilterIndex].options.findIndex(x => x.name === filterValue);
          if (foundFilterOptionIndex === -1) {
            return filterData[foundFilterIndex].options.push({
              name: filterValue,
              count: 1,
            })
          }
          filterData[foundFilterIndex].options[foundFilterOptionIndex].count += 1;
        })


      })
      
      setState({
        products: data.products,
        filteredProducts: data.products.sort(sortAlphabeticallyAZ),
        isLoading: false,
        colors: Array.from(colorSet.values()),
        colorData: swatches ? swatches.colors : [],
        groupData: swatches ? swatches.groups: [],
        filterData,
        filtersSelected: {},
      })
      console.log('component mounted.');
    })()
  }, [collectionId]);

  useEffect(() => {
    const foundColorGroup = groupData.find((x, c) => x.title === selectedColor);
    let newFilteredProducts = products
      .filter(p => {
        return !(selectedColor && foundColorGroup)
          ? true 
          : p.variants.find(v => {
            const productVariantColor = v.option1;
            const foundProductVariantColorInGroupColors = !productVariantColor 
              ? false 
              : foundColorGroup.colors.find(groupColor => 
                 groupColor.toLowerCase() === productVariantColor.toLowerCase()
              )
            return foundProductVariantColorInGroupColors;
          })
      });
    Object.keys(filtersSelected).forEach((filterName) => {
      if (!filtersSelected[filterName]) return;
      if(filterName === 'Brand'){
        newFilteredProducts = newFilteredProducts.filter(p => 
          filtersSelected[filterName].toLowerCase() === p.vendor.toLowerCase()
        )
        return;
      } 
      if(filterName === 'Gender'){
        newFilteredProducts = newFilteredProducts.filter(p => 
          filtersSelected[filterName].toLowerCase() === p.product_type.toLowerCase()
        )
        return;
      } 
      newFilteredProducts = newFilteredProducts.filter(p => {
        let foundMatch = false;
        p.tags.split(',').forEach((tag) => {
          if (tag.indexOf('__filter:') === -1) return;
          const [currentFilterName, currentFilterValue] = tag.split('__filter:')[1].split('--');
          if (filterName === currentFilterName && filtersSelected[filterName] === currentFilterValue) {
            foundMatch = true;
          }
        });
        return foundMatch;
      })
    })
    setState(state => {
      return ({ ...state, filteredProducts: newFilteredProducts })
    });
  }, [selectedColor, filtersSelected, products, groupData])

  const getSortingMethod = (sortOption: SortOptions) => {
    let sortingMethod = sortAlphabeticallyAZ;
    switch (sortOption) {
      case SortOptions.ALPHABETICALLY_AZ:
        break;
      case SortOptions.ALPHABETICALLY_ZA:
        sortingMethod = sortAlphabeticallyZA
        break;
      case SortOptions.DATE_NEW_TO_OLD:
        sortingMethod = sortDateNewToOld
        break;
      case SortOptions.DATE_OLD_TO_NEW:
        sortingMethod = sortDateOldToNew
        break;
      case SortOptions.PRICE_HIGH_TO_LOW:
        sortingMethod = sortPriceHighToLow
        break;
      case SortOptions.PRICE_LOW_TO_HIGH:
        sortingMethod = sortPriceLowToHigh
        break;
      default:
        sortingMethod = sortAlphabeticallyAZ
    }
    return sortingMethod;
  };

  const setSortingMethod = (sortOption: SortOptions) => {
    let sortedFilteredProducts = filteredProducts.sort(getSortingMethod(sortOption));
    setAppliedSortingMethod(sortOption);
    setState({
      ...state,
      filteredProducts: sortedFilteredProducts
    });
  };
  const setFilterSelected = (filterName: string, filterOptionName: string) => {
    const newState = {
      ...state,
      filtersSelected: { ...filtersSelected },
    };
    newState.filtersSelected[filterName] = filterOptionName;
    setState(newState);
    const openFiltersNew = { ...openFilters };
    openFiltersNew[filterName] = false;
    setOpenFilters(openFiltersNew);
  }

  const clearAllFilters = () => {
    setState({
      ...state,
      filtersSelected: {},
    })
    setSelectedColor('');
  }

  return (
    <div className={s.MainContainer}>
      <FilterPanel
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        colors={colors}
        colorData={colorData}
        groupData={groupData}
        filteredProducts={filteredProducts}
        selectedColor={selectedColor}
        allProducts={products}
        setSelectedColor={setSelectedColor}
        filterData={filterData}
        filtersSelected={filtersSelected}
        setFilterSelected={setFilterSelected}
        clearAllFilters={clearAllFilters}
      />
      <div className={[s.ProductsContainer, isLoading ? s.TextCenter : false].filter(Boolean).join(' ')} >
        <div className={s.AppliedFilterSortBar}>
          <div className={s.AppliedFiltersSection}></div>
          <div className={[s.Sorting].join(' ')}>
            <div className={s.VerticalCenter}>
              <h4>Sort:</h4>
            </div>
            <div className={s.VerticalCenter}>
              <select
                value={appliedSortingMethod}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setSortingMethod(event.target.value as SortOptions)}
                className={s.SortingSelect}
              >
                <option selected={appliedSortingMethod === SortOptions.ALPHABETICALLY_AZ} value={SortOptions.ALPHABETICALLY_AZ}>Alphabetically, A-Z</option>
                <option selected={appliedSortingMethod === SortOptions.ALPHABETICALLY_ZA} value={SortOptions.ALPHABETICALLY_ZA}>Alphabetically, Z-A</option>
                <option selected={appliedSortingMethod === SortOptions.PRICE_LOW_TO_HIGH} value={SortOptions.PRICE_LOW_TO_HIGH}>Price, low to high</option>
                <option selected={appliedSortingMethod === SortOptions.PRICE_HIGH_TO_LOW} value={SortOptions.PRICE_HIGH_TO_LOW}>Price, high to low</option>
                <option selected={appliedSortingMethod === SortOptions.DATE_OLD_TO_NEW} value={SortOptions.DATE_OLD_TO_NEW}>Date, old to new</option>
                <option selected={appliedSortingMethod === SortOptions.DATE_NEW_TO_OLD} value={SortOptions.DATE_NEW_TO_OLD}>Date, new to old</option>
              </select>
            </div>
          </div>
        </div>
        {isLoading
          ? <img alt="loading-spinner" src={loading} className={s.LoadingImage} />
          : <div className={s.Products}>
            {
              filteredProducts.map((product, j) =>
                <a key={`product-${j}`} className={s.Product} href={`${window.location.href}/products/${product.handle}`}>
                  <div className={s.ProductImageContainer} >
                    {product.image
                      ? <LazyLoad>
                        <img alt={`${s.ProductName} Product`} className={s.ProductImage} src={product.image.src} />
                      </LazyLoad>
                      : <img alt={`${s.ProductName} Product`} className={s.ProductImage} src={notFound} />
                    }
                  </div>
                  <div className={s.ProductInfoContainer} >
                    <div className={s.ProductName} >{product.title}</div>
                  </div>
                  <div className={s.ProductColorsContainer}>
                    <div className={s.ProductPrice} >${product.variants[0].price}</div>
                    <div className={s.ProductColors} >
                      {
                        product.variants.map((v, m) => {
                          const foundSwatch = colorData.find(swatch => swatch.color === v.option1);
                          if (!foundSwatch) { return ('') }
                          return (<img key={`swatch-${j}-${m}`} alt={foundSwatch.color + ' Swatch'} className={s.ProductSwatchImage} src={foundSwatch.file} />)
                        })
                      }
                    </div>
                  </div>
                </a>
              )
            }
          </div>
        }
      </div>
    </div>
  );
}

export default CategoryDisplay;
